import { useContext, useEffect, useRef, useState } from "react";
import { RealtimeContext } from "./RealtimeProvider";
import { post as commsPost } from "../../service/comms/commsApi";
import { getRoomRegistry, releaseRoomRegistry, ensureRoomSetup } from "./roomChannelRegistry";

/**
 * Subscribe to a room's Centrifugo channel (plus its coach sub-channel).
 *
 * The channel name ALWAYS comes from the server (subscription-token response);
 * it is never built client-side. Events are thin — handlers receive the raw
 * `ctx.data` and are expected to react by re-fetching.
 *
 * Multiple callers (e.g. a global unread tracker and an open Thread) may
 * subscribe to the same roomId concurrently — the underlying subscription is
 * shared via roomChannelRegistry.js so only one real Centrifuge subscription
 * (and one token fetch) exists per room at a time.
 *
 * @param {string|number} roomId
 * @param {{
 *   onMessage?: (data: any) => void,
 *   onRead?: (data: any) => void,
 *   onDelivered?: (data: any) => void,
 *   onTyping?: (data: any) => void,
 *   onParticipant?: (data: any) => void,
 *   onAiSettings?: (data: any) => void,
 *   onFlag?: (data: any) => void,
 *   onSla?: (data: any) => void,
 *   onTicketViewing?: (data: any) => void,
 * }} handlers
 * @returns {{ canPublish: boolean, ready: boolean, getPresence: () => Promise<any> }}
 */
export function useRoomChannel(roomId, handlers = {}) {
  const { centrifuge, connected } = useContext(RealtimeContext);
  const [canPublish, setCanPublish] = useState(false);
  const [ready, setReady] = useState(false);

  // Keep handlers in a ref so re-renders don't trigger resubscription.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  // Expose a lazy presence fetch that closes over the current subscription.
  const getPresenceRef = useRef(async () => ({}));

  useEffect(() => {
    if (!roomId || !centrifuge || !connected) {
      return undefined;
    }

    let cancelled = false;
    const registry = getRoomRegistry(centrifuge, roomId);

    const dispatch = (data) => {
      if (!data || typeof data.type !== "string") {
        return;
      }
      const h = handlersRef.current;
      switch (data.type) {
        case "message":
          h.onMessage?.(data);
          break;
        // An EXISTING message changed server-side — today, an event card that a
        // newer card for the same booking has replaced.
        case "message.updated":
          h.onMessageUpdated?.(data);
          break;
        case "read":
          h.onRead?.(data);
          break;
        case "delivered":
          h.onDelivered?.(data);
          break;
        case "typing":
          h.onTyping?.(data);
          break;
        case "participant":
          h.onParticipant?.(data);
          break;
        case "ai_settings":
          h.onAiSettings?.(data);
          break;
        case "flag":
          h.onFlag?.(data);
          break;
        case "sla":
          h.onSla?.(data);
          break;
        // Support ticket collision detection. The events table names this
        // TICKET_VIEWING while the room events it sits alongside are all
        // lowercase, so accept either spelling off the wire.
        case "ticket_viewing":
        case "TICKET_VIEWING":
          h.onTicketViewing?.(data);
          break;
        default:
          break;
      }
    };

    registry.listeners.add(dispatch);
    getPresenceRef.current = async () => {
      try {
        return await registry.sub?.presence();
      } catch (err) {
        console.error("Room presence failed:", err);
        return {};
      }
    };

    const fetchToken = async () => {
      const res = await commsPost(`/api/v1/rooms/${roomId}/token`);
      return res?.data;
    };

    ensureRoomSetup(centrifuge, roomId, registry, fetchToken)
      .then(() => {
        if (cancelled) return;
        setCanPublish(registry.canPublish);
        setReady(true);
      })
      .catch((err) => {
        console.error("Room channel subscription failed:", err);
        if (!cancelled) {
          setReady(false);
        }
      });

    return () => {
      cancelled = true;
      setReady(false);
      setCanPublish(false);
      getPresenceRef.current = async () => ({});
      registry.listeners.delete(dispatch);
      releaseRoomRegistry(centrifuge, roomId, registry);
    };
  }, [roomId, centrifuge, connected]);

  return {
    canPublish,
    ready,
    getPresence: () => getPresenceRef.current(),
  };
}
